import type { CSSObject } from '@emotion/react'
import { useEffect, useRef, useState } from 'react'
import { DefaultPasswordRules } from '@opallabs/principles'
import { TextInputType } from '../TextInput'
import type { TextInputValidationResult } from '../TextInput'
import { TextInputWithLabel } from '../TextInputWithLabel'
import type { TextInputImperativeHandle } from '../TextInputWithLabel'
import { screenreaderOnly } from '../utils'
import * as styles from './styles'
import type { PasswordInputsProps } from './types'

export type { PasswordInputsProps }

/**
 * This managed component provides a pair of password inputs, with validation
 * for the password structure, and ensures that the inputs have matching
 * values.
 *
 * The component intentionally has no styling except trivial rules for the
 * `<fieldset>`, so layout is entirely up to the consuming component.
 */
export const PasswordInputs = ({
  explicitErrors = {},
  onChange,
  onValidate,
  rules = DefaultPasswordRules,
  styleOverrides = {},
  theme = 'light',
}: PasswordInputsProps): JSX.Element => {
  const [password, setPassword] = useState<string>('')
  const [passwordConfirmation, setPasswordConfirmation] = useState<string>('')
  // The inputs are initially empty, and thus invalid.
  const [validPassword, setValidPassword] = useState<boolean>(false)
  const [validPasswordConfirmation, setValidPasswordConfirmation] =
    useState<boolean>(false)
  const [
    forcePasswordConfirmationValidation,
    setForcePasswordConfirmationValidation,
  ] = useState<boolean>(false)
  const passwordRef = useRef<TextInputImperativeHandle>(null)
  const passwordConfirmationRef = useRef<TextInputImperativeHandle>(null)

  useEffect(() => {
    if (onChange) onChange(password)
  }, [onChange, password, passwordConfirmation])

  useEffect(() => {
    const valid = validPassword && validPasswordConfirmation
    onValidate(valid)
  }, [onValidate, validPassword, validPasswordConfirmation])

  return (
    <fieldset css={[styles.container, styleOverrides?.container]}>
      <legend css={screenreaderOnly}>Password</legend>

      <TextInputWithLabel
        autocompleteHint="new-password"
        explicitError={explicitErrors.new_password}
        labelText="Password"
        minLength={rules?.minimumLength}
        onChange={(value: string): void => {
          setPassword(value)
          passwordConfirmationRef.current?.forceValidation()
        }}
        onValidate={(newValid: boolean): void => setValidPassword(newValid)}
        passwordCharacterPatterns={rules.characterPatterns}
        ref={passwordRef}
        required={true}
        styleOverrides={{
          container: {
            ...styleOverrides?.input,
            ...styleOverrides?.password,
          } as CSSObject,
        }}
        theme={theme}
        type={TextInputType.Password}
      />
      <TextInputWithLabel
        autocompleteHint="new-password"
        disabled={password === ''}
        explicitError={explicitErrors.confirm_password}
        forceValidation={forcePasswordConfirmationValidation}
        labelText="Confirm Password"
        minLength={rules?.minimumLength}
        onChange={(value: string): void => {
          setPasswordConfirmation(value)
          passwordRef.current?.forceValidation()
        }}
        onValidate={(newValid: boolean): void => {
          setForcePasswordConfirmationValidation(
            password !== passwordConfirmation
          )
          setValidPasswordConfirmation(newValid)
        }}
        ref={passwordConfirmationRef}
        required={true}
        styleOverrides={{
          container: {
            ...styleOverrides?.input,
            ...styleOverrides?.passwordConfirmation,
          } as CSSObject,
        }}
        theme={theme}
        type={TextInputType.Password}
        validator={(value: string): TextInputValidationResult => ({
          errorMessage: 'Passwords must match',
          valid: value === password,
        })}
      />
    </fieldset>
  )
}
PasswordInputs.displayName = 'Lapidary: PasswordInputs'
